import { Client } from '@hey-api/client-fetch';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { AxiosResponse } from 'axios';
import {
  listallComments,
  ListallCommentsResponse,
  ListallCommentsData,
  createaNewComment,
  CreateaNewCommentData,
  CreateaNewCommentResponse,
  getCommentDetails,
  deleteComment,
  updateComment,
  UpdateCommentData,
  postAddattachments,
  PostAddattachmentsResponse,
  AddCommentAttachmentsRequest,
  getListallattachments,
  GetListallattachmentsResponse,
} from '@data-access/connect/tc-tcps';

import {
  IComment,
  ICreateCommentRequest,
  IUpdateCommentRequest,
  IDeleteCommentRequest,
  IGetCommentsRequest,
  ICommentsResponse,
  ICommentResponse,
  ObjectType,
} from '../models/comment.types';

// Constants
const DEFAULT_COMMENTS_LIMIT = 50;

// API Response Types (inferred from usage patterns)
interface ApiCommentUser {
  readonly status?: string;
  readonly id?: string;
  readonly tiduuid?: string;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
}

interface ApiCommentItem {
  readonly id?: string;
  readonly description?: string;
  readonly objectId?: string;
  readonly objectType?: string;
  readonly contextId?: string;
  readonly createdOn?: string;
  readonly createdBy?: ApiCommentUser;
  readonly modifiedOn?: string;
  readonly modifiedBy?: ApiCommentUser;
  readonly hasAttachment?: boolean;
}

// Additional interfaces for FILE attachment handling
export interface ISnapshotDetails {
  readonly snapshotType: string;
  readonly snapshotData?: string;
  readonly snapshotUrl?: string;
}

export interface ICommentAttachmentDetails {
  readonly snapshot: ISnapshotDetails;
}

export interface ICommentAttachments {
  readonly id: string;
  readonly imgUrl: string;
  readonly title: string;
  readonly type: string;
}

export interface ITopicViewpointResponse {
  readonly guid: string;
  readonly viewId: string;
  readonly snapshot: ISnapshotDetails;
}

export interface ICreateTopicViewpointRequest {
  readonly projectId: string;
  readonly objectId: string;
  readonly payload: ICommentAttachmentDetails;
}

export interface IEditTopicCommentRequest {
  readonly projectId: string;
  readonly objectId: string;
  readonly commentId: string;
  readonly comment: string;
  readonly viewpointGuid?: string;
}

// Helper functions to transform between API types and internal types
const transformApiCommentToInternal = (
  apiComment: CreateaNewCommentResponse
): IComment => {
  return {
    id: apiComment.id,
    description: apiComment.description,
    objectId: apiComment.objectId,
    objectType: apiComment.objectType as ObjectType, // API returns string, but interface expects ObjectType
    contextId: apiComment.contextId, // contextId is not in CommentDetailsResponse
    createdOn: apiComment.createdOn,
    createdBy: {
      status: apiComment.createdBy.status,
      id: apiComment.createdBy.id || '',
      tiduuid: apiComment.createdBy.tiduuid || '',
      email: apiComment.createdBy.email,
      firstName: apiComment.createdBy.firstName,
      lastName: apiComment.createdBy.lastName,
    },
    modifiedOn: apiComment.modifiedOn,
    modifiedBy: {
      status: apiComment.modifiedBy.status,
      id: apiComment.modifiedBy.id || '',
      tiduuid: '', // ModifiedBy doesn't have tiduuid
      email: apiComment.modifiedBy.email,
      firstName: apiComment.modifiedBy.firstName,
      lastName: apiComment.modifiedBy.lastName,
    },
    hasAttachment: apiComment.hasAttachment, // hasAttachment is not in CommentDetailsResponse, will be determined by attachment API call
  };
};

// Separate transformation for list API response which might have different structure
const transformApiCommentListItemToInternal = (
  apiComment: ApiCommentItem
): IComment => {
  return {
    id: apiComment.id || '',
    description: apiComment.description,
    objectId: apiComment.objectId,
    objectType: apiComment.objectType as ObjectType | undefined, // API returns string, but interface expects ObjectType
    contextId: apiComment.contextId,
    createdOn: apiComment.createdOn,
    createdBy: apiComment.createdBy
      ? {
          status: apiComment.createdBy.status || '',
          id: apiComment.createdBy.id || '',
          tiduuid: apiComment.createdBy.tiduuid || '',
          email: apiComment.createdBy.email || '',
          firstName: apiComment.createdBy.firstName || '',
          lastName: apiComment.createdBy.lastName || '',
        }
      : undefined,
    modifiedOn: apiComment.modifiedOn,
    modifiedBy: apiComment.modifiedBy
      ? {
          status: apiComment.modifiedBy.status || '',
          id: apiComment.modifiedBy.id || '',
          tiduuid: '', // ModifiedBy doesn't have tiduuid
          email: apiComment.modifiedBy.email || '',
          firstName: apiComment.modifiedBy.firstName || '',
          lastName: apiComment.modifiedBy.lastName || '',
        }
      : undefined,
    hasAttachment: apiComment.hasAttachment || false,
  };
};

const transformApiCommentsListToInternal = (
  apiComments: ListallCommentsResponse,
  page: number,
  limit: number
): ICommentsResponse => {
  return {
    comments: apiComments.map(transformApiCommentListItemToInternal),
    total: apiComments.length, // Note: API doesn't provide total count, using array length
    page,
    limit,
  };
};

export class CommentApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient,
    private readonly createTopicViewpointFn?: (
      projectId: string,
      topicId: string,
      payload: ICommentAttachmentDetails
    ) => Promise<AxiosResponse<ITopicViewpointResponse>>,
    private readonly editTopicCommentFn?: (
      projectId: string,
      topicId: string,
      commentId: string,
      payload: { comment: string; viewpointGuid?: string }
    ) => Promise<AxiosResponse<IComment>>
  ) {}
  // Query for listing all comments for an issue
  public getCommentsQuery(
    options: Accessor<IGetCommentsRequest>
  ): UseQueryResult<ICommentsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['comments', options()],
      queryFn: async (): Promise<ICommentsResponse> => {
        const requestOptions = options();
        const apiData: ListallCommentsData = {
          query: {
            objectId: requestOptions.objectId,
            objectType: requestOptions.objectType,
            contextId: requestOptions.contextId,
            // Note: API uses cursor-based pagination, not page-based
            // May need to adapt pagination logic
          },
        };

        const response = await listallComments({
          client: this.client,
          ...apiData,
        });

        if (response.error) {
          throw new Error('Failed to fetch comments');
        }

        return transformApiCommentsListToInternal(
          response.data!,
          requestOptions.page || 1,
          requestOptions.limit || DEFAULT_COMMENTS_LIMIT
        );
      },
      enabled: !!options().objectId && !!options().projectId,
    }));
  }

  // Query for getting a specific comment
  public getCommentQuery(
    commentId: Accessor<string | undefined>
  ): UseQueryResult<ICommentResponse, Error> {
    return useQuery(() => ({
      queryKey: ['comment', commentId()],
      queryFn: async (): Promise<ICommentResponse> => {
        const id = commentId();
        if (!id) {
          throw new Error('Comment ID is required');
        }

        const response = await getCommentDetails({
          client: this.client,
          path: {
            commentId: id,
          },
        });

        if (response.error) {
          throw new Error('Failed to get comment');
        }

        return { comment: transformApiCommentToInternal(response.data!) };
      },
      enabled: !!commentId(),
    }));
  }

  // Mutation for creating a new comment
  public createCommentMutation(): UseMutationResult<
    ICommentResponse,
    Error,
    ICreateCommentRequest
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: ICreateCommentRequest
      ): Promise<ICommentResponse> => {
        const apiData: CreateaNewCommentData = {
          body: {
            objectId: data.objectId,
            objectType: data.objectType,
            description: data.comment,
            contextId: data.contextId,
          },
        };

        const response = await createaNewComment({
          client: this.client,
          ...apiData,
        });

        if (response.error) {
          throw new Error('Failed to create comment');
        }

        return { comment: transformApiCommentToInternal(response.data!) };
      },
      onSuccess: (_, variables) => {
        // Invalidate comments list query
        void this.queryClient.invalidateQueries({
          queryKey: [
            'comments',
            { issueId: variables.objectId, projectId: variables.projectId },
          ],
        });
      },
    }));
  }

  // Mutation for updating a comment
  public updateCommentMutation(): UseMutationResult<
    ICommentResponse,
    Error,
    IUpdateCommentRequest
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: IUpdateCommentRequest
      ): Promise<ICommentResponse> => {
        const apiData: UpdateCommentData = {
          path: {
            commentId: data.commentId,
          },
          body: {
            description: data.comment,
          },
        };

        const response = await updateComment({
          client: this.client,
          ...apiData,
        });

        if (response.error) {
          throw new Error('Failed to update comment');
        }

        return { comment: transformApiCommentToInternal(response.data!) };
      },
      onSuccess: (data, variables) => {
        // Invalidate comments list and specific comment queries
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['comment', variables.commentId],
        });

        // Update the specific comment in cache
        this.queryClient.setQueryData(['comment', variables.commentId], data);
      },
    }));
  }

  // Mutation for deleting a comment
  public deleteCommentMutation(): UseMutationResult<
    void,
    Error,
    IDeleteCommentRequest
  > {
    return useMutation(() => ({
      mutationFn: async (data: IDeleteCommentRequest): Promise<void> => {
        const response = await deleteComment({
          client: this.client,
          path: {
            commentId: data.commentId,
          },
        });

        if (response.error) {
          throw new Error('Failed to delete comment');
        }
      },
      onSuccess: (_, variables) => {
        // Invalidate comments list and remove specific comment from cache
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
        this.queryClient.removeQueries({
          queryKey: ['comment', variables.commentId],
        });
      },
    }));
  }

  // Helper method to get comments for a specific issue
  public async getComments(
    request: IGetCommentsRequest
  ): Promise<ICommentsResponse> {
    const apiData: ListallCommentsData = {
      query: {
        objectId: request.objectId,
        objectType: request.objectType,
        contextId: request.contextId,
      },
    };

    const response = await listallComments({
      client: this.client,
      ...apiData,
    });

    if (response.error) {
      throw new Error('Failed to fetch comments');
    }

    return transformApiCommentsListToInternal(
      response.data!,
      request.page || 1,
      request.limit || DEFAULT_COMMENTS_LIMIT
    );
  }

  // Helper method to get a specific comment
  public async getComment(commentId: string): Promise<ICommentResponse> {
    const response = await getCommentDetails({
      client: this.client,
      path: {
        commentId,
      },
    });

    if (response.error) {
      throw new Error('Failed to fetch comment');
    }

    return { comment: transformApiCommentToInternal(response.data!) };
  }

  public async createComment(
    request: ICreateCommentRequest
  ): Promise<ICommentResponse> {
    const apiData: CreateaNewCommentData = {
      body: {
        objectId: request.objectId,
        objectType: request.objectType,
        description: request.comment,
        contextId: request.contextId,
      },
    };

    const response = await createaNewComment({
      client: this.client,
      ...apiData,
    });

    if (response.error) {
      throw new Error('Failed to create comment');
    }

    return { comment: transformApiCommentToInternal(response.data!) };
  }

  // Helper method to update a comment
  public async updateComment(
    request: IUpdateCommentRequest
  ): Promise<ICommentResponse> {
    const apiData: UpdateCommentData = {
      path: {
        commentId: request.commentId,
      },
      body: {
        description: request.comment,
      },
    };

    const response = await updateComment({
      client: this.client,
      ...apiData,
    });

    if (response.error) {
      throw new Error('Failed to update comment');
    }

    return { comment: transformApiCommentToInternal(response.data!) };
  }

  // Helper method to delete a comment
  public async deleteComment(request: IDeleteCommentRequest): Promise<void> {
    const response = await deleteComment({
      client: this.client,
      path: {
        commentId: request.commentId,
      },
    });

    if (response.error) {
      throw new Error('Failed to delete comment');
    }
  }

  // Query for getting comment attachments
  public getCommentAttachmentsQuery(
    commentId: Accessor<string | undefined>
  ): UseQueryResult<GetListallattachmentsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['commentAttachments', commentId()],
      queryFn: async (): Promise<GetListallattachmentsResponse> => {
        const id = commentId();
        if (!id) {
          throw new Error('Comment ID is required');
        }

        const response = await getListallattachments({
          client: this.client,
          path: { commentId: id },
        });

        if (response.error) {
          throw new Error('Failed to fetch comment attachments');
        }

        return response.data!;
      },
      enabled: !!commentId(),
    }));
  }

  // Helper method to get comment attachments
  public async getCommentAttachments(
    commentId: string
  ): Promise<GetListallattachmentsResponse> {
    const response = await getListallattachments({
      client: this.client,
      path: { commentId },
    });

    if (response.error) {
      throw new Error('Failed to fetch comment attachments');
    }

    return response.data!;
  }

  // Helper method to delete comment attachment
  public async deleteCommentAttachment(
    commentId: string,
    attachmentId: string,
    attachmentType: string
  ): Promise<void> {
    // Use direct client call with body instead of query parameters
    // as the API description indicates it should use request body
    const response = await this.client.delete({
      url: `/comments/${commentId}/attachments`,
      body: [
        {
          id: attachmentId,
          type: attachmentType,
        },
      ],
    });

    if (response.error) {
      throw new Error('Failed to delete comment attachment');
    }
  }

  // Helper method to create topic viewpoint for FILE attachments
  public async createTopicViewpoint(
    request: ICreateTopicViewpointRequest
  ): Promise<ITopicViewpointResponse> {
    if (!this.createTopicViewpointFn) {
      throw new Error('createTopicViewpoint function not provided');
    }

    const response = await this.createTopicViewpointFn(
      request.projectId,
      request.objectId,
      request.payload
    );

    return response.data;
  }

  // Helper method to edit topic comment with viewpoint
  public async editTopicComment(
    request: IEditTopicCommentRequest
  ): Promise<IComment> {
    if (!this.editTopicCommentFn) {
      throw new Error('editTopicComment function not provided');
    }

    const response = await this.editTopicCommentFn(
      request.projectId,
      request.objectId,
      request.commentId,
      {
        comment: request.comment,
        viewpointGuid: request.viewpointGuid,
      }
    );

    return response.data;
  }

  // New method to create comment attachments using the modern API
  public createCommentAttachmentsMutation(): UseMutationResult<
    PostAddattachmentsResponse,
    Error,
    {
      readonly commentId: string;
      readonly attachments: readonly AddCommentAttachmentsRequest[];
    }
  > {
    return useMutation(() => ({
      mutationFn: async (data): Promise<PostAddattachmentsResponse> => {
        const response = await postAddattachments({
          client: this.client,
          path: { commentId: data.commentId },
          body: [...data.attachments],
        });

        if (!response.data) {
          throw new Error('Failed to create comment attachments');
        }

        return response.data;
      },
      onSuccess: (_, variables) => {
        // Invalidate comment attachments query to ensure the UI updates with new attachments
        void this.queryClient.invalidateQueries({
          queryKey: ['commentAttachments', variables.commentId],
        });
        // Also invalidate comments list to ensure hasAttachment flag is updated
        void this.queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
      },
    }));
  }

  // New method to delete comment attachments
  public deleteCommentAttachmentsMutation(): UseMutationResult<
    void,
    Error,
    {
      readonly commentId: string;
      readonly attachmentId: string;
      readonly attachmentType: string;
    }
  > {
    return useMutation(() => ({
      mutationFn: async (data): Promise<void> => {
        // Use direct client call with body instead of query parameters
        // as the API description indicates it should use request body
        const response = await this.client.delete({
          url: `/comments/${data.commentId}/attachments`,
          body: [
            {
              id: data.attachmentId,
              type: data.attachmentType,
            },
          ],
        });

        if (response.error) {
          throw new Error('Failed to delete comment attachment');
        }
      },
      onSuccess: (_, variables) => {
        // Invalidate comment attachments query to refresh the list
        void this.queryClient.invalidateQueries({
          queryKey: ['commentAttachments', variables.commentId],
        });
      },
    }));
  }

  // Mutation for creating viewpoint and updating comment (for FILE attachments)
  // @deprecated Use createCommentAttachmentsMutation instead
  public createFileAttachmentMutation(): UseMutationResult<
    IComment,
    Error,
    {
      readonly projectId: string;
      readonly objectId: string;
      readonly commentId: string;
      readonly comment: string;
      readonly attachmentsDetails: readonly ICommentAttachmentDetails[];
    }
  > {
    return useMutation(() => ({
      mutationFn: async (data): Promise<IComment> => {
        // Process each attachment detail to create viewpoints
        const viewpointResponses = await Promise.all(
          data.attachmentsDetails.map((attachmentDetail) =>
            this.createTopicViewpoint({
              projectId: data.projectId,
              objectId: data.objectId,
              payload: attachmentDetail,
            })
          )
        );

        // Use the first viewpoint GUID to update the comment
        const viewpointGuid = viewpointResponses[0]?.guid;

        return await this.editTopicComment({
          projectId: data.projectId,
          objectId: data.objectId,
          commentId: data.commentId,
          comment: data.comment,
          viewpointGuid: viewpointGuid,
        });
      },
      onSuccess: (_, variables) => {
        // Invalidate comments list query
        void this.queryClient.invalidateQueries({
          queryKey: [
            'comments',
            { issueId: variables.objectId, projectId: variables.projectId },
          ],
        });
      },
    }));
  }
}
